import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import {
  Box,
  Grid,
  Typography,
  Paper,
  Button,
  Skeleton,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { LinkOffOutlined } from '@mui/icons-material';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import useVerify from 'app/hooks/useVerify';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

const getDisplayName = (name) => {
  if (!name) return '-';
  let cleaned = name.replace(/\s*\[[^\]]*]/g, '').trim();

  return cleaned
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

const QrViewer = () => {
  const { VerifyQrCode } = useVerify();
  const [searchParams] = useSearchParams();
  const t = searchParams.get('t');
  const [project, setProject] = useState(null);
  const [isValid, setValid] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    const QrChecking = async () => {
      try {
        const res = await VerifyQrCode(t);
        if (res?.success) {
          setValid(true);
          setProject(res.payload);
        } else {
          setValid(true);
          toast.error("Your document can't be previewed, please contact your administrator!", {
            position: 'bottom-center',
          });
        }
      } catch (err) {
        setValid(true);
        toast.error("Your document can't be previewed, please contact your administrator!", {
          position: 'bottom-center',
        });
      }
    };
    QrChecking();
  }, [t]);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #ee7752, #e73c7e, #23a6d5, #23d5ab)',
        backgroundSize: '400% 400%',
        animation: 'gradientAnimation 15s ease infinite',
        width: '100%',
        overflow: 'auto',
        p: 2,
      }}
    >
      <style>
        {`
          @keyframes gradientAnimation {
            0% { background-position: 0% 50%; }
            50% { background-position: 100% 50%; }
            100% { background-position: 0% 50%; }
          }
        `}
      </style>

      <Paper
        sx={{
          p: { xs: 2, sm: 3, md: 4 },
          borderRadius: 3,
          width: '100%',
          maxWidth: 1200,
          textAlign: 'center',
        }}
      >
        <Box sx={{ mb: 3, textAlign: 'center' }}>
          <img
            src="/assets/images/logo-dashboard.png"
            alt="Logo"
            style={{ width: '100%', maxWidth: 180, height: 'auto' }}
          />
        </Box>

        {project ? (
          <Grid container spacing={3}>
            <Grid item xs={12} md={5} sx={{ textAlign: 'left' }}>
              <Typography variant="h6" sx={{ textAlign: { xs: 'center', md: 'left' } }} gutterBottom>
                DOCUMENT INFORMATION
              </Typography>
              <Grid container spacing={1}>
                {[
                  { label: 'No Dokumen', value: `${project?.CompanyCode}/${project?.code}`},
                  { label: 'Perihal', value: project?.title },
                  { label: 'Deskripsi', value: project?.description },
                  {
                    label: 'Tgl. Buat',
                    value: dayjs(project?.created_at || '').format('DD MMMM YYYY HH:mm'),
                  },
                  {
                    label: 'Tgl. Cetak',
                    value: dayjs(project?.printed_at || '').format('DD MMMM YYYY HH:mm'),
                  },
                  { label: 'Dicetak oleh', value: project?.printed_by },
                  {
                    label: 'Status',
                    value: project?.status === 3 ? 'APPROVED' : '-',
                    color: project?.status === 3 ? 'success.main' : 'error.main',
                  },
                ].map((item, index) => (
                  <React.Fragment key={index}>
                    <Grid item xs={4}>
                      <Typography variant="body2">{item.label}</Typography>
                    </Grid>
                    <Grid item xs={8}>
                      <Typography variant="body2" color={item.color || 'text.primary'}>
                        : {item.value || '-'}
                      </Typography>
                    </Grid>
                  </React.Fragment>
                ))}
              </Grid>
            </Grid>

            <Grid item xs={12} md={7}>
              {project?.templates && (
                <>
                  <Box sx={{ overflowX: 'auto', mb: 2 }}>
                    <table className="table table-bordered table-sm">
                      <thead className="bg-dark text-white text-center">
                        <tr>
                          <th colSpan="4">DOCUMENT REQUESTED BY</th>
                        </tr>
                        <tr>
                          <th>#</th>
                          <th>NAMA LENGKAP</th>
                          <th>JABATAN</th>
                          <th>TANGGAL ENTRY</th>
                        </tr>
                      </thead>
                      <tbody>
                        {project.templates.map((a, i) => (
                          <tr key={`assigner-${i}`}>
                            <td>{i + 1}</td>
                            <td>{getDisplayName(a?.assigner?.employee_name)}</td>
                            <td>{a?.assigner?.position || '-'}</td>
                            <td>
                              {a?.assigner?.date
                                ? dayjs(a.assigner.date).format('DD MMMM YYYY HH:mm')
                                : '-'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </Box>

                  <Box sx={{ overflowX: 'auto', mb: 2 }}>
                    <table className="table table-bordered table-sm">
                      <thead className="bg-dark text-white text-center">
                        <tr>
                          <th colSpan="4">DOCUMENT VERIFIED BY</th>
                        </tr>
                        <tr>
                          <th>#</th>
                          <th>NAMA LENGKAP</th>
                          <th>JABATAN</th>
                          <th>TANGGAL VERIFY</th>
                        </tr>
                      </thead>
                      <tbody>
                        {project.templates.map((a, i) => (
                          <tr key={`verificator-${i}`}>
                            <td>{i + 1}</td>
                            <td>{getDisplayName(a?.verificator?.employee_name)}</td>
                            <td>{a?.verificator?.position || '-'}</td>
                            <td>
                              {a?.verificator?.date
                                ? dayjs(a.verificator.date).format('DD MMMM YYYY HH:mm')
                                : '-'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </Box>
                </>
              )}

              {project?.approvers && (
                <Box sx={{ overflowX: 'auto', mb: 2 }}>
                  <table className="table table-bordered table-sm">
                    <thead className="bg-dark text-white text-center">
                      <tr>
                        <th colSpan="5">DOCUMENT APPROVED BY</th>
                      </tr>
                      <tr>
                        <th>#</th>
                        <th>NAMA LENGKAP</th>
                        <th>JABATAN</th>
                        <th>CATATAN</th>
                        <th>TANGGAL APPROVED</th>
                      </tr>
                    </thead>
                    <tbody>
                      {project.approvers.map((a, i) => (
                        <tr key={`approver-${i}`}>
                          <td>{i + 1}</td>
                          <td>{getDisplayName(a?.employee_name)}</td>
                          <td>{a?.position || '-'}</td>
                          <td>{a?.note || '-'}</td>
                          <td>
                            {a?.date ? dayjs(a.date).format('DD MMMM YYYY HH:mm') : '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </Box>
              )}
            </Grid>

            <Grid item xs={12}>
              <Button
                fullWidth={isMobile}
                sx={{ mt: 2 }}
                variant="contained"
                color="error"
                component={Link}
                to="/dashboard"
              >
                GO TO DASHBOARD
              </Button>
            </Grid>
          </Grid>
        ) : isValid ? (
          <>
            <LinkOffOutlined style={{ fontSize: 40, color: '#c62828' }} />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              This link is not valid, please try again later!
            </Typography>
            <Button
              fullWidth={isMobile}
              sx={{
                mt: 3,
                color: 'white',
                '&:hover': {
                  color: 'white',
                  backgroundColor: '#c62828',
                },
              }}
              component={Link}
              to="/session/signin"
              variant="contained"
              color="error"
            >
              GO TO LOGIN PAGE
            </Button>
          </>
        ) : (
          <>
            <Skeleton variant="text" width={200} height={40} sx={{ mx: 'auto', mb: 2 }} />
            <Skeleton variant="text" width={300} height={25} sx={{ mx: 'auto', mb: 2 }} />
            <Skeleton variant="rectangular" height={100} sx={{ mb: 3, borderRadius: 2 }} />
            <Skeleton variant="rounded" width={150} height={36} sx={{ mx: 'auto' }} />
          </>
        )}
      </Paper>
    </Box>
  );
};

export default QrViewer;
